import React from "react";
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  getWeek,
} from "date-fns";
import { SLAstatusMap } from "../constants/constants";
import {
  McTag,
  McTextAndIcon,
  McTooltip,
} from "@maersk-global/mds-react-wrapper";

/** True when casestatusreason (string or comma-separated) includes the given reason text. */
export const caseStatusReasonIncludes = (casestatusreason, targetReason) => {
  if (!targetReason) return false;
  const needle = String(targetReason).trim().toLowerCase();
  if (!needle) return false;
  if (!casestatusreason) return false;

  const parts = Array.isArray(casestatusreason)
    ? casestatusreason.map((r) => String(r).trim())
    : String(casestatusreason)
        .split(",")
        .map((r) => r.trim());

  return parts.some((p) => p && p.toLowerCase() === needle);
};

export const formattedTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getAuthToken = () => {
  return sessionStorage.getItem("authToken");
};

export const extractDate = (message) => {
  if (!message) return null;

  const dateRegex =
    /(\d{1,2}(?:st|nd|rd|th)?\s(?:January|February|March|April|May|June|July|August|September|October|November|December),\s\d{4})\s*\|\s*(\d{1,2}:\d{2}\s(?:AM|PM))/i;
  const match = message.match(dateRegex);

  if (match) {
    return `${match[1]} | ${match[2]}`;
  }

  return null;
};


export const formatHeader = (key, formatType) => {
  switch (key) {
    case "casenumber":
      return "Case ID";
    case "blnumber":
      return "Bl Number";
    case "casestatus":
      return "Case Status";
    case "slastatus":
      return "SLA Status";
    case "casetat":
      return "Case TAT (Responded~Received)";
    case "slaremainingtime":
      return "Time Remaining to SLA";
    case "dacaseprocessingtime":
      return "Case Processing  Time (Responded~Created)";
    case "casereceiveddatetime":
      return "Time Recieved At ";
    case "casestatusdatetime":
      return "Time Responded At";
    // case "createddate":
    //   return "Case Created At";
    case "targetsla":
      return "Target SLA";
    case "countryslatime":
      return "Country SLA Time";
    case "customername":
      return "Customer Name";
    case "afrdata":
      return "AFR Data";
    case "bookingdata":
      return "Booking Data";
    case "casestatusreason":
      return "Status Reason";
    case "sourcename":
      return "Source Name";
    case "email_subject":
      return "Email Subject";
    case "modifiedby":
      return "Modified By";
    case "caseassignedto":
      return "Case Assigned To";
    // case "modifieddate":
    //   return "Modified Date";
    case "ie_modifieddate":
      return "Email Received Modified Date";

    default:
      if (formatType === "lowercase") {
        return key.toLowerCase();
      }
      return (
        key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1")
      );
  }
};

export const getOrdinalSuffix = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

export const formatDateAndTime = (dateTimeString) => {
  const utcString = dateTimeString.endsWith("Z")
    ? dateTimeString
    : dateTimeString + "Z";

  const utcDate = new Date(utcString);

  if (isNaN(utcDate)) {
    throw new Error("Invalid date string");
  }

  const localDate = new Date(utcDate.toLocaleString("en-US"));

  const day = localDate.getDate();
  const dayWithSuffix = day + getOrdinalSuffix(day);

  const month = localDate.toLocaleString("default", { month: "long" });

  const year = localDate.getFullYear();

  const options = { hour: "2-digit", minute: "2-digit", hour12: true };
  const time = localDate.toLocaleTimeString("en-US", options);

  return `${dayWithSuffix} ${month}, ${year} | ${time}`;
};

export const formatDateAndTimeUTC = (dateTimeString) => {
  if (!dateTimeString) return '';
  const iso = dateTimeString.endsWith('Z') ? dateTimeString : dateTimeString + 'Z';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const day = d.getUTCDate();
  const dayWithSuffix = day + getOrdinalSuffix(day);
  const month = d.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
  const year = d.getUTCFullYear();
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  return `${dayWithSuffix} ${month}, ${year} | ${hours}:${minutes} UTC`;
};

export const convertMinutesToTime = (totalTimeInString = "") => {
  if (typeof totalTimeInString !== "string") {
    totalTimeInString = String(totalTimeInString); // Convert non-string values to a string
  }

  const totalTimeInIntMin = parseInt(totalTimeInString.replace(" min", ""), 10);

  if (isNaN(totalTimeInIntMin)) {
    return ""; // Return an empty string if the value is NaN
  }

  const isNegative = totalTimeInIntMin < 0;
  const absoluteMinutes = Math.abs(totalTimeInIntMin);

  const days = Math.floor(absoluteMinutes / 1440);
  const remainingMinutesAfterDays = absoluteMinutes % 1440;

  const hours = Math.floor(remainingMinutesAfterDays / 60);
  const minutes = remainingMinutesAfterDays % 60;

  const dayText = days > 0 ? `${days} d` : "";
  const hourText = hours > 0 ? `${hours} h` : "";
  const minuteText = minutes > 0 ? `${minutes} m` : "";

  const result =
    (isNegative ? "-" : "") +
    [dayText, hourText, minuteText].filter(Boolean).join(" ");

  return result.trim();
};

export const formatTimeToAvailableValues = (timeString = "") => {
  const timeParts = timeString
    .split(" ")
    .map((part) => part.replace(/\D/g, ""));

  const [days, hours, minutes, seconds] = timeParts.map(Number);

  const timeArray = [
    // days > 0 ? `${days} day${days > 1 ? "s" : ""}` : "",
    // hours > 0 ? `${hours} hour${hours > 1 ? "s" : ""}` : "",
    // minutes > 0 ? `${minutes} min` : "",
    // seconds > 0 ? `${seconds} sec` : "",
    days > 0 ? `${days} d` : "",
    hours > 0 ? `${hours} h` : "",
    minutes > 0 ? `${minutes} m` : "",
    seconds > 0 ? `${seconds} s` : "",
  ].filter(Boolean);

  return !timeString ? "" : timeArray.join(" ");
};

export const formatStatus = (status) =>
  SLAstatusMap[status] ||
  status
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

export const getOutcomeClass = (outcome) =>
  outcome?.toUpperCase() === "NON COMPLIANT"
    ? "NON-COMPLIANT"
    : outcome?.toUpperCase();

export const renderWithTooltip = (value, maxVisible = 1, withTag = false) => {
  const parts = Array.isArray(value) ? value : value.split(",");
  const visibleParts = parts.slice(0, maxVisible);
  const additionalParts = parts.slice(maxVisible);

  return (
    <div className="custom-cell-display-row">
      {visibleParts.map((part, index) =>
        withTag && value ? (
          <McTag key={index} fit="small" appearance="error">
            {part}
          </McTag>
        ) : (
          <span key={index}>{part}</span>
        )
      )}

      {additionalParts.length > 0 && (
        <McTooltip opendelay={50} appearance="neutral-default" zindex={99999}>
          <McTag appearance="success" className="mc-tag-display" slot="trigger">
            <McTextAndIcon
              label={additionalParts.length}
              icon="plus"
              size="16"
            />
          </McTag>
          <span>{additionalParts.join(", ")}</span>
        </McTooltip>
      )}
    </div>
  );
};

export const descendingComparator = (a, b, orderBy) => {
  let valueA = a[orderBy];
  let valueB = b[orderBy];

  // Special handling for casenumber - parse as integer
  if (orderBy === "casenumber") {
    valueA = parseStrToInt(valueA);
    valueB = parseStrToInt(valueB);
  }
  // Special handling for date fields - parse as Date for proper comparison
  else if (orderBy === "ie_modifieddate" || orderBy === "casereceiveddatetime" || orderBy === "casestatusdatetime") {
    valueA = valueA ? new Date(valueA).getTime() : 0;
    valueB = valueB ? new Date(valueB).getTime() : 0;
  }

  if (valueB < valueA) {
    return -1;
  }
  if (valueB > valueA) {
    return 1;
  }
  return 0;
};
export const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

export const transformValidationStatusData = (data) => {
  return data.map((item) => {
    return {
      Validation: item.rulename,
      afrData: item.afrdata,
      bookingdata: item.bookingdata,
      Outcome: item.outcome,
      reason:item.reason,
    };
  });
};

export const formatEmailContent = (mailContent) => {
  const normalized =
    typeof mailContent === "string"
      ? mailContent
      : mailContent
      ? JSON.stringify(mailContent)
      : "";
  const responseLines = normalized.split("\n") || [];

  if (responseLines.length === 0) return null;

  const firstLine = responseLines[0]; // First line
  let secondLine = responseLines[2] || ""; // Second line (if exists)

  // Find the first occurrence of ":"
  const colonIndex = secondLine.indexOf(",");
  let secondLinePart1 = secondLine;
  let secondLinePart2 = "";

  if (colonIndex !== -1) {
    secondLinePart1 = secondLine.substring(0, colonIndex + 1); // Text before `:`
    secondLinePart2 = secondLine.substring(colonIndex + 1).trim(); // Text after `:`
  }

  // Get remaining lines and format them
  const remainingLines = responseLines
    .slice(2)
    .map((line, index) =>
      line.trim() === "" ? <br key={index} /> : <p key={index}>{line}</p>
    );

  return (
    <>
      <p>{secondLinePart1}</p>
      <p>{secondLinePart2}</p>
      {remainingLines}
    </>
  );
};

export const calculateTimeDifference = (initialDate, finalDate) => {
  if (!initialDate || !finalDate) {
    return "N/A";
  }

  const initial = new Date(initialDate);
  const final = new Date(finalDate);

  const days = differenceInDays(initial, final);
  const hours = differenceInHours(initial, final) % 24;
  const minutes = differenceInMinutes(initial, final) % 60;
  const seconds = differenceInSeconds(initial, final) % 60;

  return `${days}d ${hours}h ${minutes}m ${seconds}s`;
};

export const getWeekNumber = (createdDate) => {
  if (!createdDate) {
    return "N/A";
  }
  const created = new Date(createdDate);
  return getWeek(created);
};

export const parseStrToInt = (valueInString = "") => {
  if (typeof valueInString !== "string") {
    valueInString = String(valueInString); // Convert non-string values to a string
  }
  const parts = valueInString.split(" ");
  return parseInt(parts[0], 10) || 0; // Return 0 if parsing fails
};
